import { useState } from 'react'
import Button from './Button.jsx'
import Popover from './Popover.jsx'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// A day cell shows at most this many lines of events on the desktop grid; past
// that it shows (MAX - 1) events plus a "+N more" button that lists them all.
// Cells are a fixed size (calendar.css), so this cap is what keeps busy days
// from overflowing. CSS does the hiding, so the phone agenda still lists every
// event and never shows the button.
export const MAX_EVENT_LINES = 3

// Month calendar. Caller supplies title, nav, and per-day data (with event
// nodes). Desktop = 7-col grid of uniform cells; phone (≤640px) = day agenda
// (empty days hidden).
export default function Calendar({
  title, onPrev, onNext, weekdayLabels = WEEKDAYS, leadingBlanks = 0, days = [], renderDay,
}) {
  const [openKey, setOpenKey] = useState(null)
  // Complete the last week so the grid is a full rectangle.
  const trailingBlanks = (7 - ((leadingBlanks + days.length) % 7)) % 7

  return (
    <div>
      <div className="cn-cal-head">
        <Button variant="subtle" size="sm" onClick={onPrev} aria-label="Previous month">‹</Button>
        <span className="cn-cal-title">{title}</span>
        <Button variant="subtle" size="sm" onClick={onNext} aria-label="Next month">›</Button>
      </div>
      <div className="cn-cal-weekdays">
        {weekdayLabels.map((w) => <div key={w} className="cn-cal-weekday">{w}</div>)}
      </div>
      <div className="cn-cal">
        {Array.from({ length: leadingBlanks }, (_, i) => <div key={`b${i}`} className="cn-cal-blank" />)}
        {days.map((d, i) => {
          const events = Array.isArray(d.events) ? d.events : d.events ? [d.events] : []
          const overflow = events.length > MAX_EVENT_LINES
          const hidden = events.length - (MAX_EVENT_LINES - 1)
          // Fri/Sat open the list leftward so it stays on screen.
          const nearRightEdge = (leadingBlanks + i) % 7 >= 5
          return (
            <div key={d.key} className="cn-cal-cell" data-empty={d.empty ? 'true' : 'false'}
              data-today={d.today ? 'true' : 'false'} data-overflow={overflow ? 'true' : 'false'}>
              {renderDay ? renderDay(d) : (
                <>
                  <span className="cn-cal-daynum">{d.label}</span>
                  <div className="cn-cal-events">
                    {events.map((ev, j) => <div key={ev?.key ?? j} className="cn-cal-event">{ev}</div>)}
                  </div>
                  {overflow && (
                    <span className="cn-popover-wrap cn-cal-more-wrap">
                      <button type="button" className="cn-cal-more" aria-haspopup="dialog" aria-expanded={openKey === d.key}
                        onClick={() => setOpenKey(openKey === d.key ? null : d.key)}>
                        +{hidden} more
                      </button>
                      <Popover open={openKey === d.key} onClose={() => setOpenKey(null)}
                        align={nearRightEdge ? 'right' : 'left'} className="cn-cal-more-list">
                        <div className="cn-cal-more-title">{d.label}</div>
                        {events.map((ev, j) => <div key={ev?.key ?? j} className="cn-cal-event">{ev}</div>)}
                      </Popover>
                    </span>
                  )}
                </>
              )}
            </div>
          )
        })}
        {Array.from({ length: trailingBlanks }, (_, i) => <div key={`t${i}`} className="cn-cal-blank" />)}
      </div>
    </div>
  )
}
